import { ExternalLink, Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { getNotebooklmJobs, type NotebooklmJob } from '@/api/api'
import { Button } from '@/components/ui/button'
import { parseUtcDate } from '@/lib/date-utils'
import { useNotebooklmStore } from '@/store/useNotebooklmStore'

const STATUS_LABEL: Record<NotebooklmJob['status'], string> = {
  pending: '順番待ち',
  running: '実行中',
  completed: '完了',
  failed: '失敗',
}

const STATUS_CLASS: Record<NotebooklmJob['status'], string> = {
  pending: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40',
  running: 'bg-sky-500/20 text-sky-300 border-sky-500/40',
  completed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  failed: 'bg-red-500/20 text-red-300 border-red-500/40',
}

const formatElapsed = (from: string, to: string | null): string => {
  const start = parseUtcDate(from).getTime()
  const end = to ? parseUtcDate(to).getTime() : Date.now()
  const seconds = Math.max(Math.floor((end - start) / 1000), 0)
  if (seconds < 60) return `${seconds}秒`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}分${seconds % 60}秒`
  return `${Math.floor(minutes / 60)}時間${minutes % 60}分`
}

const formatDateTime = (iso: string): string =>
  parseUtcDate(iso).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })

export function NotebooklmJobsTab() {
  const [jobs, setJobs] = useState<NotebooklmJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const liveJobs = useNotebooklmStore((s) => s.jobs)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setJobs(await getNotebooklmJobs())
      setError(null)
    } catch (e) {
      console.error('Failed to load NotebookLM jobs:', e)
      setError('ジョブの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // 進捗そのものは SSE で重ねるので、ジョブの増減とステータス変化のときだけ取り直す
  const liveSignature = [...liveJobs.values()].map((j) => `${j.id}:${j.status}`).join(',')
  useEffect(() => {
    if (!liveSignature) return
    void load()
  }, [liveSignature, load])

  // 実行中の経過時間を進めるための再描画
  const [, setTick] = useState(0)
  const hasRunning = jobs.some((j) => j.status === 'pending' || j.status === 'running')
  useEffect(() => {
    if (!hasRunning) return
    const timer = setInterval(() => setTick((v) => v + 1), 1000)
    return () => clearInterval(timer)
  }, [hasRunning])

  const merged = jobs.map((job) => {
    const live = liveJobs.get(job.product_id)
    if (!live || live.id !== job.id) return job
    return {
      ...job,
      status: live.status,
      progress: live.progress,
      processed_sources: live.processedSources,
      total_sources: live.totalSources,
      message: live.message || job.message,
    }
  })

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-xs text-muted-foreground">
          {loading ? '読み込み中...' : `${merged.length}件`}
          {hasRunning && ' / 実行中あり'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          更新
        </Button>
      </div>

      {error && <p className="px-3 py-2 text-sm text-destructive">{error}</p>}

      <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-2">
        {!loading && merged.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">取り込み履歴はありません</p>
        )}

        {merged.map((job) => {
          const isActive = job.status === 'pending' || job.status === 'running'
          return (
            <div key={job.id} className="rounded-lg border border-border bg-card p-3">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium wrap-break-word">
                  {job.product_title ?? `作品 #${job.product_id}`}
                </span>
                <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] ${STATUS_CLASS[job.status]}`}>
                  {STATUS_LABEL[job.status]}
                </span>
              </div>

              {isActive && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{job.message || '準備中'}</span>
                    <span>
                      {job.progress}%{job.total_sources > 0 && ` (${job.processed_sources}/${job.total_sources})`}
                    </span>
                  </div>
                  <div className="mt-1 h-1 rounded bg-muted">
                    <div className="h-full rounded bg-sky-400 transition-all" style={{ width: `${job.progress}%` }} />
                  </div>
                </div>
              )}

              {!isActive && job.message && <p className="mt-1 text-xs text-muted-foreground">{job.message}</p>}
              {job.status === 'failed' && job.error && <p className="mt-1 text-xs text-destructive">{job.error}</p>}

              <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span>
                  {formatDateTime(job.created_at)}
                  {job.started_at && ` / 経過 ${formatElapsed(job.started_at, job.completed_at)}`}
                </span>
                {job.notebook_url && (
                  <a
                    href={job.notebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 text-sky-400"
                  >
                    <ExternalLink size={12} />
                    開く
                  </a>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
